/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service module for managing bike bus operations including creation,
management, passenger tracking, and cancellation notifications.
*/

import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { parseISO } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { enGB } from "date-fns/locale";

// Note: This service assumes bike bus tables exist in Supabase
// Tables needed: bike_buses, bike_bus_stops, bike_bus_passengers

// Define bike bus types
export type BikeBusStatus = "pending" | "completed" | "cancelled";

export interface BikeBusStop {
  id: string;
  bikeBusId: string;
  index: number;
  siteId: string;
  siteName?: string;
  stop: string;
  time: string; // ISO timestampz string
  createdAt: Date;
  updatedAt: Date;
}

export interface BikeBus {
  bikeBusId: string;
  leader: string;
  date: string; // ISO timestampz string
  status: BikeBusStatus;
  stops: BikeBusStop[];
  memberCount: number;
  isCurrentUserMember?: boolean;
  currentUserMembership?: {
    status: string;
    startStopId?: string;
    endStopId?: string;
    bookingId?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface BikeBusMember {
  bikeBusId: string;
  userId: string;
  bookingId?: string;
  startStopId?: string;
  endStopId?: string;
  status: string; // bike_bus_member_status
  userName: string;
  userEmail: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface BikeBusDetails extends BikeBus {
  members: BikeBusMember[];
}

// Bike bus service for managing bike bus operations
export const bikeBusService = {
  // Get all bike buses with pagination and member counts
  getBikeBuses: async (page: number = 1, pageSize: number = 10, currentUserId?: string): Promise<{ bikeBuses: BikeBus[]; total: number }> => {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // First get bike buses with stops
      const { data: bikeBusData, error: bikeBusError, count } = await supabase
        .from('bike_bus')
        .select(`
          *,
          bike_bus_stops (
            id,
            bike_bus_id,
            index,
            site_id,
            stop,
            time,
            created_at,
            updated_at,
            sites (
              name
            )
          )
        `, { count: 'exact' })
        .range(from, to)
        .order('date', { ascending: false });

      if (bikeBusError) throw bikeBusError;

      if (!bikeBusData) {
        return { bikeBuses: [], total: 0 };
      }

      // Get member counts for all bike buses in this page
      const bikeBusIds = bikeBusData.map(bus => bus.id);
      const { data: memberCounts, error: memberError } = await supabase
        .from('bike_bus_members')
        .select('bike_bus_id')
        .in('bike_bus_id', bikeBusIds);

      if (memberError) throw memberError;

      // Get current user's memberships if userId provided
      let userMemberships: any[] = [];
      if (currentUserId) {
        const { data: userMemberData, error: userMemberError } = await supabase
          .from('bike_bus_members')
          .select('bike_bus_id, status, start_stop_id, end_stop_id, booking_id')
          .eq('user_id', currentUserId)
          .in('bike_bus_id', bikeBusIds);

        if (userMemberError) throw userMemberError;
        userMemberships = userMemberData || [];
      }

      // Count members per bike bus
      const memberCountMap = new Map<string, number>();
      (memberCounts || []).forEach(member => {
        const count = memberCountMap.get(member.bike_bus_id) || 0;
        memberCountMap.set(member.bike_bus_id, count + 1);
      });

      // Create user membership map
      const userMembershipMap = new Map<string, any>();
      userMemberships.forEach(membership => {
        userMembershipMap.set(membership.bike_bus_id, membership);
      });

      const bikeBuses: BikeBus[] = bikeBusData.map(bus => {
        const membership = userMembershipMap.get(bus.id);
        return {
          bikeBusId: bus.id,
          leader: bus.leader,
          date: bus.date,
          status: bus.status as BikeBusStatus,
          memberCount: memberCountMap.get(bus.id) || 0,
          isCurrentUserMember: !!membership,
          currentUserMembership: membership ? {
            status: membership.status,
            startStopId: membership.start_stop_id,
            endStopId: membership.end_stop_id,
            bookingId: membership.booking_id
          } : undefined,
          stops: bus.bike_bus_stops?.map(stop => ({
            id: stop.id,
            bikeBusId: stop.bike_bus_id,
            index: stop.index,
            siteId: stop.site_id,
            siteName: stop.sites?.name,
            stop: stop.stop,
            time: stop.time,
            createdAt: new Date(stop.created_at),
            updatedAt: new Date(stop.updated_at)
          })) || [],
          createdAt: new Date(bus.created_at),
          updatedAt: new Date(bus.updated_at)
        };
      });

      return { bikeBuses, total: count || 0 };
    } catch (error) {
      console.error('Error fetching bike buses:', error);
      toast.error('Failed to fetch bike buses');
      throw error;
    }
  },

  // Get a single bike bus with passengers
  getBikeBus: async (id: string): Promise<BikeBusDetails> => {
    try {
      const { data, error } = await supabase
        .from('bike_bus')
        .select(`
          *,
          bike_bus_stops (
            id,
            bike_bus_id,
            index,
            site_id,
            stop,
            time,
            created_at,
            updated_at,
            sites (
              name
            )
          ),
          bike_bus_members (
            bike_bus_id,
            user_id,
            booking_id,
            start_stop_id,
            end_stop_id,
            status,
            created_at,
            updated_at,
            users (
              username
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Bike bus not found');

      const bikeBus: BikeBusDetails = {
        bikeBusId: data.id,
        leader: data.leader,
        date: data.date,
        status: data.status as BikeBusStatus,
        memberCount: data.bike_bus_members?.length || 0,
        stops: data.bike_bus_stops?.map(stop => ({
          id: stop.id,
          bikeBusId: stop.bike_bus_id,
          index: stop.index,
          siteId: stop.site_id,
          siteName: stop.sites?.name,
          stop: stop.stop,
          time: stop.time,
          createdAt: new Date(stop.created_at),
          updatedAt: new Date(stop.updated_at)
        })) || [],
        members: data.bike_bus_members?.map(member => ({
          bikeBusId: member.bike_bus_id,
          userId: member.user_id,
          bookingId: member.booking_id,
          startStopId: member.start_stop_id,
          endStopId: member.end_stop_id,
          status: member.status,
          userName: member.users?.username || '',
          userEmail: '', // Email not available in users table
          createdAt: new Date(member.created_at),
          updatedAt: new Date(member.updated_at)
        })) || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

      return bikeBus;
    } catch (error) {
      console.error('Error fetching bike bus:', error);
      toast.error('Failed to fetch bike bus details');
      throw error;
    }
  },

  // Create a new bike bus
  createBikeBus: async (bikeBusData: {
    leader: string;
    date: string;
    stops: Omit<BikeBusStop, 'bikeBusId' | 'index' | 'createdAt' | 'updatedAt'>[];
  }): Promise<BikeBus> => {
    try {
      // Insert bike bus
      // Fix date to avoid timezone conversion issues
      const dateStr = bikeBusData.date.split('T')[0]; // Get YYYY-MM-DD
      const localDate = `${dateStr}T12:00:00`; // Use noon to avoid timezone edge cases
      
      const { data: bikeBusResult, error: bikeBusError } = await supabase
        .from('bike_bus')
        .insert([{
          leader: bikeBusData.leader,
          date: localDate,
          status: 'pending'
        }])
        .select()
        .single();

      if (bikeBusError) throw bikeBusError;
      if (!bikeBusResult) throw new Error('Failed to create bike bus');

      // Insert stops - combine date with time to create full timestamp in London time
      const stopsToInsert = bikeBusData.stops.map((stop, index) => {
        // Combine the bike bus date with the stop time to create a full timestamp
        const dateStr = bikeBusData.date.split('T')[0]; // Get YYYY-MM-DD
        const timeStr = stop.time; // HH:MM format (24-hour)
        
        // Create datetime as London time and convert to UTC
        // Input: 8:00 PM (20:00) on 2025-07-30 should be treated as London time
        
        // Parse date and time components
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hour, minute] = timeStr.split(':').map(Number);
        
        // Validate input
        if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
          throw new Error(`Invalid date/time components: ${dateStr} ${timeStr}`);
        }
        
        // Create a "naive" Date object (no timezone assumption)
        // This represents the date/time as if it's in London  
        const naiveDate = new Date(year, month - 1, day, hour, minute, 0);
        
        // Validate naive date creation
        if (isNaN(naiveDate.getTime())) {
          throw new Error(`Invalid naive date created from: ${year}-${month}-${day} ${hour}:${minute}`);
        }
        
        // Use fromZonedTime to convert from London time to UTC
        // This treats naiveDate as if it's in Europe/London timezone
        const utcDate = fromZonedTime(naiveDate, 'Europe/London');
        
        // Validate UTC conversion
        if (isNaN(utcDate.getTime())) {
          throw new Error(`Invalid UTC date after conversion from London time`);
        }
        
        // Convert to ISO string for database (removes 'Z' suffix for timestampz compatibility)
        const utcISOString = utcDate.toISOString().slice(0, 19);
        
        // Validation: Test round-trip conversion to ensure it works
        const testUTCDate = new Date(`${utcISOString}Z`);
        const testLondonTime = testUTCDate.toLocaleTimeString('en-GB', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          minute: '2-digit', 
          hour12: false
        });
        
        // Ensure round-trip conversion matches input
        if (testLondonTime !== timeStr) {
          console.warn(`Timezone conversion issue: Input ${timeStr} London → ${testLondonTime} display`);
        }
        
        return {
          bike_bus_id: bikeBusResult.id,
          index: index + 1, // Start from 1 for first stop
          site_id: stop.siteId,
          stop: stop.stop,
          time: utcISOString
        };
      });

      const { error: stopsError } = await supabase
        .from('bike_bus_stops')
        .insert(stopsToInsert);

      if (stopsError) throw stopsError;
      
      // Return the created bike bus
      return await bikeBusService.getBikeBus(bikeBusResult.id);
    } catch (error) {
      console.error('Error creating bike bus:', error);
      toast.error('Failed to create bike bus');
      throw error;
    }
  },

  // Update bike bus (only allowed fields)
  updateBikeBus: async (id: string, updates: {
    leader?: string;
    status?: BikeBusStatus;
  }): Promise<BikeBus> => {
    try {
      const updateData: { leader?: string; status?: BikeBusStatus } = {};
      if (updates.leader !== undefined) updateData.leader = updates.leader;
      if (updates.status !== undefined) updateData.status = updates.status;

      const { error } = await supabase
        .from('bike_bus')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      toast.success('Bike bus updated successfully');
      return await bikeBusService.getBikeBus(id);
    } catch (error) {
      console.error('Error updating bike bus:', error);
      toast.error('Failed to update bike bus');
      throw error;
    }
  },

  // Cancel bike bus using edge function
  cancelBikeBus: async (id: string): Promise<void> => {
    try {
      // First get the bike bus details to get passenger list for UI feedback
      const bikeBus = await bikeBusService.getBikeBus(id);
      
      // Call the cancel_bike_bus edge function
      const { data, error } = await supabase.functions.invoke('cancel_bike_bus', {
        body: { bike_bus_id: id }
      });

      if (error) throw error;

      toast.success(`Bike bus cancelled and ${bikeBus.members.length} members notified`);
    } catch (error) {
      console.error('Error cancelling bike bus:', error);
      toast.error('Failed to cancel bike bus');
      throw error;
    }
  },

  // Delete bike bus and notify passengers (legacy - use cancelBikeBus instead)
  deleteBikeBus: async (id: string): Promise<void> => {
    try {
      // First get the bike bus details to get passenger list
      const bikeBus = await bikeBusService.getBikeBus(id);
      
      // Delete the bike bus (cascade will handle stops and members)
      const { error } = await supabase
        .from('bike_bus')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // TODO: Send cancellation notifications to members
      // This would typically involve email/push notifications
      console.log(`Bike bus ${id} cancelled. Notifications should be sent to:`, 
        bikeBus.members.map(m => m.userEmail));

      toast.success(`Bike bus cancelled and ${bikeBus.members.length} members notified`);
    } catch (error) {
      console.error('Error deleting bike bus:', error);
      toast.error('Failed to cancel bike bus');
      throw error;
    }
  },

  // Get available sites for bike bus stops
  getSites: async (): Promise<{ id: string; name: string; organizationName: string }[]> => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select(`
          id,
          name,
          organization:organizations (
            name
          )
        `)
        .order('name');

      if (error) throw error;

      return data?.map(site => ({
        id: site.id,
        name: site.name,
        organizationName: site.organization?.name || 'Unknown'
      })) || [];
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Failed to fetch sites');
      throw error;
    }
  }
}; 